import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { Hide, Show } from "@/assets/icons/icons";
import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { Notification } from "@/components/Interface/Notification/Notification";
import { PasswordStrength } from "@/components/Interface/PasswordStrength/PasswordStrength";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { FieldRow } from "@/components/Portal/UserAccount/PersonalDetails/FieldRow/FieldRow";
import { useAlert } from "@/hooks/useAlert";
import { useIsInSecuritySubFlow } from "@/hooks/useIsInSecuritySubFlow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { setTokens } from "@/hooks/useToken";
import { useGetCustomer } from "@/services/api";
import { useChangePassword, useChangeUsername } from "@/services/auth";
import { BasicCustomerData } from "@/services/model";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import { BACKEND_EMAIL_REGEX } from "@/utils/constants";
import {
  getPasswordRequirementTokens,
  getPasswordValidationError,
} from "@/utils/passwordValidation";

type EmailForm = { currentEmail: string; newEmail: string; password: string };
type PasswordForm = { currentPassword: string; newPassword: string };

const PasswordVisibilityToggle = ({
  visible,
  onToggle,
  label,
}: {
  visible: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <button
    type="button"
    className="flex items-center text-foreground-primary focus:outline-none"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onToggle}
    aria-label={label}
  >
    {visible ? (
      <Hide width={24} height={24} />
    ) : (
      <Show width={24} height={24} />
    )}
  </button>
);

export const LoginData: FCC = () => {
  const isMobile = useIsMobile();
  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);
  const isEditingTwoFactor = useIsInSecuritySubFlow();
  const isEditingOutsideLoginData =
    userAccountState.editingPersonalData || isEditingTwoFactor;
  const { t } = useTranslation();
  const showAlert = useAlert();
  const {
    data: customer,
    isLoading: isGettingCustomer,
    refetch,
  } = useGetCustomer();

  const [showEmailConfirmPassword, setShowEmailConfirmPassword] =
    useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const passwordRequirements = useMemo(
    () => getPasswordRequirementTokens(t),
    [t],
  );

  const passwordToggleLabel = (visible: boolean) =>
    t(
      visible
        ? "translation:label.hidePassword"
        : "translation:label.showPassword",
    );

  const { mutate: updateCustomerMail, isLoading: isUpdatingMail } =
    useChangeUsername({
      mutation: {
        onSuccess: (data) => {
          // @ts-ignore
          setTokens(data.access_token, data.refresh_token, data.verifier);
          setUserAccountState((prev) => ({ ...prev, editingMail: false }));
          emailForm.reset();
          refetch();
          showAlert({
            type: AlertTypes.success,
            text: t(
              "user-account.personalDetails.loginData.emailChangeSuccess",
            ),
          });
        },
        onError: (error) => {
          if (
            `${error.response?.data}`.includes(
              "no user found for user identifier",
            )
          ) {
            emailForm.setError("currentEmail", {
              type: "backendError",
            });
          } else if (
            `${error.response?.data}`.includes("invalid credentials")
          ) {
            emailForm.setError("password", {
              type: "backendError",
            });
          } else if (
            `${error.response?.data}`.includes("username already in use")
          ) {
            emailForm.setError("newEmail", {
              type: "backendError",
            });
          } else if (
            `${error.response?.data}`.includes("Invalid Email Address")
          ) {
            emailForm.setError("newEmail", {
              type: "invalidEmailError",
            });
          } else {
            emailForm.setError("currentEmail", {
              type: "backendError",
            });
          }
        },
      },
    });
  const { mutate: updateCustomerPassword, isLoading: isUpdatingPassword } =
    useChangePassword({
      mutation: {
        onSuccess: (data) => {
          // @ts-ignore
          setTokens(data.access_token, data.refresh_token, data.verifier);
          setUserAccountState((prev) => ({ ...prev, editingPassword: false }));
          passwordForm.reset();
          showAlert({
            type: AlertTypes.success,
            text: t(
              "user-account.personalDetails.loginData.passwordChangeSuccess",
            ),
          });
        },
        onError: (error) => {
          if (`${error.response?.data}`.includes("invalid credentials")) {
            passwordForm.setError("currentPassword", {
              type: "backendError",
            });
          } else {
            passwordForm.setError("newPassword", {
              message: t("translation:label.validation.password"),
              type: "backendError",
            });
          }
        },
      },
    });
  const isLoading = isGettingCustomer || isUpdatingMail || isUpdatingPassword;
  const [customerData, setCustomerData] = useState<
    BasicCustomerData | undefined
  >();
  useEffect(() => {
    setCustomerData(customer);
  }, [customer]);
  const emailForm = useForm<EmailForm>({
    mode: "onChange",
    defaultValues: { currentEmail: "", newEmail: "", password: "" },
  });
  // Fills the read-only field on every entry into edit mode, not just from the
  // "Ändern" click: on mobile, SecurityView remounts this component when edit
  // mode starts, which drops anything that click handler put into the form.
  const currentEmail = customer?.email ?? "";
  useEffect(() => {
    if (userAccountState.editingMail) {
      emailForm.setValue("currentEmail", currentEmail, {
        shouldValidate: true,
      });
    }
  }, [userAccountState.editingMail, currentEmail, emailForm]);
  const passwordForm = useForm<PasswordForm>({
    mode: "onChange",
    defaultValues: { newPassword: "", currentPassword: "" },
  });

  const saveMail = (data: EmailForm) => {
    if (customerData && data) {
      updateCustomerMail({
        data: {
          oldUsername: data.currentEmail,
          newUsername: data.newEmail,
          password: data.password,
        },
      });
    }
  };
  const savePassword = (data: PasswordForm) => {
    if (customerData && data) {
      updateCustomerPassword({
        data: {
          oldPassword: data.currentPassword,
          newPassword: data.newPassword,
        },
      });
    }
  };

  // On mobile, editing the E-Mail or the Passwort is a standalone screen showing
  // only that card; on tablet/desktop the card expands inline and its
  // sibling stays visible. `SecurityView` and `UserAccount` drop the
  // account header and the tab strip for the same state.
  const isStandaloneMobileEdit =
    isMobile &&
    (userAccountState.editingMail || userAccountState.editingPassword);
  const hideEmailCard = isStandaloneMobileEdit && !userAccountState.editingMail;
  const hidePasswordCard =
    isStandaloneMobileEdit && !userAccountState.editingPassword;

  return (
    <>
      {isLoading && <LoadingSpinner />}
      <div className="flex flex-col gap-6">
        {!hideEmailCard && (
          <form onSubmit={emailForm.handleSubmit(saveMail)}>
            <ProfileCard
              title={t("user-account.personalDetails.loginData.email")}
              stickyHeader={userAccountState.editingMail}
              maxWidth="none"
              actions={
                userAccountState.editingMail ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => {
                        emailForm.reset();
                        setUserAccountState((prev) => ({
                          ...prev,
                          editingMail: false,
                        }));
                      }}
                    >
                      {t("portal:user-account.security.buttons.cancel")}
                    </Button>
                    <SubmitButton
                      fullWidth={false}
                      size="sm"
                      disabled={!emailForm.formState.isValid}
                      label={t("portal:user-account.security.buttons.save")}
                    />
                  </>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    disabled={
                      userAccountState.editingPassword ||
                      isEditingOutsideLoginData
                    }
                    onClick={() => {
                      emailForm.reset({
                        currentEmail: customerData?.email ?? "",
                        newEmail: "",
                        password: "",
                      });
                      setUserAccountState((prev) => ({
                        ...prev,
                        editingMail: true,
                      }));
                    }}
                  >
                    {t("portal:user-account.personalDetails.change")}
                  </Button>
                )
              }
            >
              {userAccountState.editingMail ? (
                <>
                  <FormField
                    id="current-email-input"
                    label={t(
                      "user-account.personalDetails.loginData.currentEmail",
                    )}
                    autocomplete="email"
                    name="currentEmail"
                    errors={emailForm.formState.errors}
                    register={emailForm.register}
                    type="email"
                    options={{
                      readOnly: true,
                      required: t("translation:label.validation.required"),
                      pattern: {
                        value: BACKEND_EMAIL_REGEX,
                        message: t("translation:label.validation.email"),
                      },
                      backendError: t(
                        "user-account.personalDetails.loginData.currentEmailError",
                      ),
                    }}
                  />

                  <FormField
                    id="new-email-input"
                    label={t("user-account.personalDetails.loginData.newEmail")}
                    autocomplete="email"
                    name="newEmail"
                    errors={emailForm.formState.errors}
                    register={emailForm.register}
                    type="email"
                    options={{
                      required: t("translation:label.validation.required"),
                      pattern: {
                        value: BACKEND_EMAIL_REGEX,
                        message: t("translation:label.validation.email"),
                      },
                      maxLength: 50,
                      backendError: t(
                        "user-account.personalDetails.loginData.emailInUseError",
                      ),
                      invalidEmailError: t(
                        "user-account.personalDetails.loginData.emailInvalid",
                      ),
                    }}
                  />

                  <FormField
                    label={t(
                      "user-account.personalDetails.loginData.passwordEnter",
                    )}
                    id="current-password-input"
                    autocomplete="current-password"
                    name="password"
                    errors={emailForm.formState.errors}
                    register={emailForm.register}
                    type={showEmailConfirmPassword ? "text" : "password"}
                    icon={
                      <PasswordVisibilityToggle
                        visible={showEmailConfirmPassword}
                        onToggle={() =>
                          setShowEmailConfirmPassword((prev) => !prev)
                        }
                        label={passwordToggleLabel(showEmailConfirmPassword)}
                      />
                    }
                    options={{
                      required: t("translation:label.validation.required"),
                      backendError: t(
                        "user-account.personalDetails.loginData.passwordError",
                      ),
                    }}
                  />
                  {!emailForm.formState.errors["password"] && (
                    <Notification
                      tone="info"
                      description={t("translation:label.hint.password")}
                    />
                  )}
                </>
              ) : (
                <FieldRow
                  dataTestid="current-email-display"
                  label={t(
                    "user-account.personalDetails.loginData.currentEmail",
                  )}
                  value={customerData?.email}
                  disabled
                />
              )}
            </ProfileCard>
          </form>
        )}

        {!hidePasswordCard && (
          <form onSubmit={passwordForm.handleSubmit(savePassword)}>
            <ProfileCard
              title={t("user-account.personalDetails.loginData.passwordTitle")}
              stickyHeader={userAccountState.editingPassword}
              maxWidth="none"
              actions={
                userAccountState.editingPassword ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => {
                        passwordForm.reset();
                        setUserAccountState((prev) => ({
                          ...prev,
                          editingPassword: false,
                        }));
                      }}
                    >
                      {t("portal:user-account.security.buttons.cancel")}
                    </Button>
                    <SubmitButton
                      fullWidth={false}
                      size="sm"
                      disabled={!passwordForm.formState.isValid}
                      label={t("portal:user-account.security.buttons.save")}
                    />
                  </>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    disabled={
                      userAccountState.editingMail || isEditingOutsideLoginData
                    }
                    onClick={() =>
                      setUserAccountState((prev) => ({
                        ...prev,
                        editingPassword: true,
                      }))
                    }
                  >
                    {t("portal:user-account.personalDetails.change")}
                  </Button>
                )
              }
            >
              {userAccountState.editingPassword ? (
                <>
                  <FormField
                    id="current-password-input"
                    label={t(
                      "user-account.personalDetails.loginData.currentPassword",
                    )}
                    autocomplete="current-password"
                    name="currentPassword"
                    errors={passwordForm.formState.errors}
                    register={passwordForm.register}
                    type={showCurrentPassword ? "text" : "password"}
                    icon={
                      <PasswordVisibilityToggle
                        visible={showCurrentPassword}
                        onToggle={() => setShowCurrentPassword((prev) => !prev)}
                        label={passwordToggleLabel(showCurrentPassword)}
                      />
                    }
                    options={{
                      required: t("translation:label.validation.required"),
                      backendError: t(
                        "user-account.personalDetails.loginData.passwordError",
                      ),
                    }}
                  />
                  <div>
                    <FormField
                      id="new-password-input"
                      label={t(
                        "user-account.personalDetails.loginData.newPassword",
                      )}
                      autocomplete="new-password"
                      passwordRules="minlength: 8; maxlength: 30; required: lower; required: upper; required: digit; required: [!#$%&*?@];"
                      name="newPassword"
                      errors={passwordForm.formState.errors}
                      register={passwordForm.register}
                      type={showNewPassword ? "text" : "password"}
                      icon={
                        <PasswordVisibilityToggle
                          visible={showNewPassword}
                          onToggle={() => setShowNewPassword((prev) => !prev)}
                          label={passwordToggleLabel(showNewPassword)}
                        />
                      }
                      options={{
                        required: t("translation:label.validation.required"),
                        validate: (value: string) =>
                          getPasswordValidationError(value, t),
                        backendError: t(
                          "translation:label.validation.password",
                        ),
                      }}
                    />
                    <div className="mt-2">
                      <PasswordStrength
                        password={passwordForm.watch("newPassword")}
                        validationTokens={passwordRequirements}
                        inverted={false}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <FieldRow
                  dataTestid="password-display"
                  label={t("user-account.personalDetails.loginData.password")}
                  value="**********************"
                  disabled
                />
              )}
            </ProfileCard>
          </form>
        )}
      </div>
    </>
  );
};
